import { useQuery } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { PagedResponse } from "../models/responses/pagedResponse";
import {
  StripeLookupResultDto,
  StripePayoutDetailDto,
  StripePayoutListItemDto,
} from "../models/responses/StripePayoutDto";
import { StripePayoutsQuery } from "../models/bodies/StripePayoutsQuery";
import {
  resolveCsvFilename,
  stripePayoutCsvFallbackName,
} from "../../utils/functions/csvFilename";
import { triggerBlobDownload } from "../../utils/functions/blobDownload";

// Stripe payouts (flag REACT_APP_STRIPE_PAYOUTS_ENABLED). Admin surface -
// every query is staleTime 0 so the screen always shows the current server
// state on entry; the list keeps the previous page visible while the next
// one loads (opt-in keepPreviousData, as the other admin lists).
export interface StripePayoutsRepository {
  usePayouts(
    query: StripePayoutsQuery,
    options?: { enabled?: boolean },
  ): Resource<PagedResponse<StripePayoutListItemDto>>;
  usePayout(payoutId: string | undefined): Resource<StripePayoutDetailDto>;
  // Only fires for a non-blank term - an empty lookup is never sent.
  useLookup(q: string): Resource<StripeLookupResultDto>;
  // GET stripe/payouts/{payoutId}/csv -> Blob download. Resolves with the
  // filename used (the server's Content-Disposition name when the browser
  // exposes it, else the same name rebuilt locally).
  downloadCsv(payoutId: string): Promise<string>;
}

export const STRIPE_PAYOUTS_ROOT_KEY = "stripePayouts";

export function useStripePayoutsRepository(): StripePayoutsRepository {
  const apiClient = useApiClient();

  function usePayouts(
    query: StripePayoutsQuery,
    options?: { enabled?: boolean },
  ): Resource<PagedResponse<StripePayoutListItemDto>> {
    return useQuery(
      [STRIPE_PAYOUTS_ROOT_KEY, "list", query],
      () => apiClient.getStripePayouts(query),
      {
        staleTime: 0,
        keepPreviousData: true,
        enabled: options?.enabled ?? true,
      },
    );
  }

  function usePayout(
    payoutId: string | undefined,
  ): Resource<StripePayoutDetailDto> {
    return useQuery(
      [STRIPE_PAYOUTS_ROOT_KEY, "detail", payoutId ?? ""],
      () => apiClient.getStripePayout(payoutId as string),
      {
        staleTime: 0,
        enabled: payoutId !== undefined && payoutId.trim() !== "",
      },
    );
  }

  function useLookup(q: string): Resource<StripeLookupResultDto> {
    const term = q.trim();
    return useQuery(
      [STRIPE_PAYOUTS_ROOT_KEY, "lookup", term],
      () => apiClient.stripeLookup(term),
      {
        staleTime: 0,
        enabled: term.length > 0,
      },
    );
  }

  async function downloadCsv(payoutId: string): Promise<string> {
    const response = await apiClient.exportStripePayoutCsv(payoutId);
    const blob = await response.blob();
    const filename = resolveCsvFilename(
      response.headers.get("Content-Disposition"),
      stripePayoutCsvFallbackName(payoutId),
    );
    triggerBlobDownload(blob, filename);
    return filename;
  }

  return {
    usePayouts,
    usePayout,
    useLookup,
    downloadCsv,
  };
}
